<template>
  <a-modal
    :open="open"
    @cancel="handleClose"
    center
    :maskClosable="false"
    :title="`Secuencia “${dashboard?.sequence_name}”`"
    :width="700"
    :bodyStyle="{ padding: '20px 0' }"
  >
    <div class="modal__body">
      <section class="card radial">
        <RadialChart
          :value="dashboard?.progress.percent?.toFixed(0)"
          label="Porcentaje de avance"
          percent
        />
      </section>

      <section class="card card--stat">
        <div class="card__icon">
          <img :src="phoneCheckIcon" alt="check" />
        </div>
        <div class="card__value">{{ dashboard?.call_statistics.answered }}</div>
        <span>Contestada</span>
      </section>

      <section class="card card--stat">
        <div class="card__icon">
          <img :src="phoneNoneIcon" alt="none" />
        </div>
        <div class="card__value">{{ dashboard?.call_statistics.no_contact }}</div>
        <span>No contactado</span>
      </section>

      <section class="card">
        <span class="card__title"> Resultado de gestiones </span>
        <CustomScrollbar height="calc(100% - 20px)">
          <ManagementResultChart :stats="dashboard?.management_statistics" />
        </CustomScrollbar>
      </section>
    </div>

    <template #footer>
      <footer>
        <a-button type="primary" ghost @click="fetchData">Actualizar datos</a-button>

        <div class="actions">
          <a-button @click="handleDownload" :loading="loading">Descargar reporte</a-button>

          <a-button type="primary" @click="handleClose">Cerrar</a-button>
        </div>
      </footer>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";

import { SequenceDashboard } from "@/@types/flows/sequences";
import { sequenceService } from "@/app/flows/sequences/services/Sequence.service";

import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import ManagementResultChart from "@/app/strategies/components/charts/ManagementResultChart.vue";
import RadialChart from "@/app/strategies/components/charts/RadialChart.vue";
import { downloadFile } from "@/app/shared/utils/file";
import { message } from "ant-design-vue";
import phoneCheckIcon from "@/app/strategies/assets/phone_check.svg?url";
import phoneNoneIcon from "@/app/strategies/assets/phone_none.svg?url";

const props = defineProps<{ sequenceId: number; open: boolean }>();

const emit = defineEmits<{ "update:open": [open: boolean] }>();

const dashboard = ref<SequenceDashboard>();

const handleClose = () => emit("update:open", false);

const fetchData = async () => {
  dashboard.value = await sequenceService.getDashboard(props.sequenceId);
};

const loading = ref(false);

const handleDownload = async () => {
  loading.value = true;

  try {
    const response = await sequenceService.downloadReport(props.sequenceId);

    downloadFile(response, `sequence-${props.sequenceId}.xlsx`, { useResponseFileName: true });
  } catch (error) {
    console.error(error);
    message.error("Ha ocurrido un error");
  }

  loading.value = false;
};

onMounted(() => fetchData());
</script>

<style scoped lang="sass">
.modal
  &__body
    color: $gray-9
    height: 400px
    display: grid
    grid-template-columns: repeat(4, 1fr)
    grid-template-rows: repeat(3, 1fr)
    gap: 12px

  &__picker
    margin-bottom: 12px

  &__paragraph
    margin: 16px 0

.radial
  display: grid
  place-content: center

  .progress
    text-align: center

.card
  border: 1px solid $gray-5
  border-radius: 6px
  padding: 5px 10px

  &__title
    font-weight: 600
    margin-bottom: 12px

  &__icon
    font-size: 32px
    margin: 4px 0

    & img
      display: block
      margin: 6px 0
      height: 36px

    &--red
      color: $red-6

  &__value
    font-size: 30px
    line-height: 38px
    margin-bottom: -4px

  &__accordion
    padding: 0

  &--stat
    display: flex
    flex-direction: column
    align-items: center
    padding: 20px 0

  &:nth-child(1)
    grid-column: 1 / 3
    grid-row: 1 / 3

  &:nth-child(2)
    grid-column: 1 / 1
    grid-row: 3 / 4

  &:nth-child(3)
    grid-column: 2 / 2
    grid-row: 3 / 4

  &:nth-child(4)
    grid-column: 3 / 5
    grid-row: 1 / 4

footer
  display: flex
  justify-content: space-between

  .actions
    display: flex
    gap: 8px
</style>